import { ExternalLink, Handshake, ListChecks, MoreHorizontal, Star, UserPlus } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { ErrorCard } from '@/components/ErrorCard'
import { AssociationCard } from '@/components/patterns/AssociationCard'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import {
  InlineProperty,
  PropertySection,
  StaticProperty,
} from '@/components/patterns/PropertySection'
import { QuickActions, type QuickAction } from '@/components/patterns/QuickActions'
import { RecordLayout } from '@/components/patterns/RecordLayout'
import { focusTab } from '@/components/patterns/recordTabModel'
import { Button } from '@/components/ui/button'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { cn } from '@/lib/utils'
import { AgreementDialog } from '../agreements/AgreementDialog'
import { useInsprekenEntry } from '../inspreken/useInsprekenEntry'
import { rhythmState } from '../agreements/format'
import { AgreementsSection } from '../agreements/AgreementsSection'
import { AgreementsTab } from '../agreements/AgreementsTab'
import { ArchiveRelationDialog } from '../agreements/ArchiveRelationDialog'
import type { Agreement } from '../agreements/types'
import { useOwnerChange } from '../agreements/useOwnerChange'
import { useRelationAgreements } from '../agreements/useRelationAgreements'
import { CategoryChip } from '../components/CategoryChip'
import { CategorySelect } from '../components/CategorySelect'
import { LabelChip, type LabelLike } from '../components/LabelChip'
import { LabelPicker } from '../components/LabelPicker'
import { SummaryEditor } from '../components/SummaryEditor'
import { CrmApiError } from '../http'
import { lastActivityText } from '../record/format'
import { ACTIVITY_ICONS, SOURCE_LABELS } from '../record/labels'
import { ListsCard } from '../record/ListsCard'
import { RecordBackLink } from '../record/RecordBackLink'
import { RelationKeyFacts } from '../record/RelationKeyFacts'
import { ActivityComposer, type ComposerRelation, type ComposerType } from '../timeline/ActivityComposer'
import { parseChip, type ChipKey } from '../timeline/chips'
import { PlannedSection } from '../timeline/PlannedSection'
import { RecentActivity } from '../timeline/RecentActivity'
import { TaskDialog } from '../timeline/TaskDialog'
import { Timeline } from '../timeline/Timeline'
import type { RelationRef } from '../timeline/types'
import { AddMemberDialog } from './AddMemberDialog'
import {
  deleteOrganisation,
  getOrganisation,
  listMembers,
  restoreOrganisation,
  setOrganisationLabels,
  updateOrganisation,
} from './api'
import { MembersTab } from './MembersTab'
import { clearCall } from '../record/callPrompt'
import { CallPromptBanner } from '../record/CallPromptBanner'
import { PhoneLink } from '../record/PhoneLink'
import { MergeNotice } from '../merges/MergeNotice'
import { MergeOrganisationsDialog } from './merge/MergeOrganisationsDialog'
import { PickOrganisationDialog } from './merge/PickOrganisationDialog'
import type { CrmMemberList, CrmOrganisation, CrmOrganisationInput } from './types'

// The tabs of the page. On a phone `RecordLayout` adds "Koppelingen" and "Gegevens"; they
// live in the URL like the others, and on a desktop they show Overzicht.
const TAB_KEYS = ['overzicht', 'contactpersonen', 'activiteit', 'afspraken', 'koppelingen', 'gegevens'] as const

type TabKey = (typeof TAB_KEYS)[number]

function parseTab(value: string | null): TabKey {
  return TAB_KEYS.find((key) => key === value) ?? 'overzicht'
}

/**
 * The query string of the page: `tab` (none for Overzicht), `type` (the chip of Activiteit) and
 * `contactpersonen=uit` while Inclusief contactpersonen is off. A change of tab drops the chip;
 * the switch is a setting of the page and stays, because Recente activiteit on Overzicht follows it.
 */
function pageSearch(tab: TabKey, type: ChipKey | null, includeMembers: boolean): URLSearchParams {
  const search = new URLSearchParams()
  if (tab !== 'overzicht') search.set('tab', tab)
  if (type) search.set('type', type)
  if (!includeMembers) search.set('contactpersonen', 'uit')
  return search
}

const menuItemClass =
  'rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted'

const selectClass = 'h-8 w-full rounded-md border border-border bg-background px-2 text-sm'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/** A clickable link only for http(s): a stored `javascript:` address must not become one. */
function webHref(url: string | null): string | null {
  return url && /^https?:\/\//i.test(url) ? url : null
}

function deleteDescription(count: number): string {
  const undo = 'Je kunt dit direct ongedaan maken.'
  if (count === 0) return undo
  if (count === 1) {
    return `De contactpersoon blijft bestaan als contact; de koppeling met deze organisatie verdwijnt. ${undo}`
  }
  return `De ${count} contactpersonen blijven bestaan als contact; hun koppeling met deze organisatie verdwijnt. ${undo}`
}

/**
 * Organisatiepagina (spec-63, design §4.4) on `RecordLayout` v2: identity, quick actions
 * (Notitie, Gesprek, Meeting, Taak, Contactpersoon) and editable properties on the left; tabs
 * Overzicht (key facts, composer, "Over …", contact persons, Gepland, recent activity),
 * Contactpersonen and Activiteit (composer and the one timeline) in the middle; the contact
 * persons and lists cards on the right; on a phone one column with the extra tabs Koppelingen
 * and Gegevens. `?tab=`, `?type=` and `?contactpersonen=uit` live in the URL. `version` reloads
 * the timeline, Gepland and the recent activity; `reloadKey` reloads the organisation itself.
 * Agreements (E4) sit on Overzicht and the tab Afspraken; merging follows in E6.
 */
export function OrganisationPage() {
  const { organisationId = '' } = useParams()
  // Another organisation is another page: nothing of the previous one (its data, a
  // half-typed edit, an open dialog) may carry over.
  return <OrganisationRecord key={organisationId} organisationId={organisationId} />
}

function OrganisationRecord({ organisationId }: { organisationId: string }) {
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const inspreken = useInsprekenEntry({ type: 'organisatie', id: organisationId })
  const tab = parseTab(params.get('tab'))
  const typeKey = parseChip(params.get('type'))
  const includeMembers = params.get('contactpersonen') !== 'uit'
  const [organisation, setOrganisation] = useState<CrmOrganisation | null>(null)
  const [members, setMembers] = useState<CrmMemberList | null>(null)
  const [loadError, setLoadError] = useState<'notfound' | 'failed' | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [version, setVersion] = useState(0)
  const [callEpoch, setCallEpoch] = useState(0)
  const [composer, setComposer] = useState<{ open: boolean; type: ComposerType; focus: boolean }>({
    open: false,
    type: 'note',
    focus: false,
  })
  const [taskOpen, setTaskOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(() => params.get('toevoegen') === '1')
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [agreementDialog, setAgreementDialog] = useState<{ open: boolean; agreement?: Agreement }>({ open: false })
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [mergePicking, setMergePicking] = useState(false)
  const [mergeWith, setMergeWith] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  // The labels as the person left them while their answer is on its way.
  const [chosenLabels, setChosenLabels] = useState<LabelLike[] | null>(null)
  const loaded = useRef(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const askedDelete = useRef(false)
  const labelSaves = useRef(0)
  const labelQueue = useRef<Promise<void>>(Promise.resolve())
  const id = useId()
  const { membersById } = useTeamMembers()
  const team = membersById
    ? [...membersById.values()].sort((a, b) => a.display_name.localeCompare(b.display_name, 'nl'))
    : []
  // The list's search, page and sort travel with the link in (`state.from`, a `?…` string).
  const fromState = (location.state as { from?: unknown } | null)?.from
  const from = typeof fromState === 'string' && fromState.startsWith('?') ? fromState : ''
  const listPath = `/relaties/organisaties${from}`
  const relation: RelationRef = { kind: 'organisation', id: organisationId }
  // A quick action's request for the editor is good for one focus: the composer reports when it
  // has honoured it and the request goes down. Left up, every later mount of the composer (the
  // tabs move it to the other panel) would take the focus again, from the tab that has it.
  const lowerFocusRequest = useCallback(
    () => setComposer((current) => (current.focus ? { ...current, focus: false } : current)),
    [],
  )

  useEffect(() => {
    let cancelled = false
    // Asked together; the members of a merged organisation are never needed, so a failure of
    // that call waits until the organisation itself has answered.
    const membersAnswer = listMembers(organisationId, { include_former: true }).then(
      (list) => ({ list }),
      (error: unknown) => ({ error }),
    )
    getOrganisation(organisationId)
      .then(async (found) => {
        if (cancelled) return
        if ('moved_to' in found) {
          // Merged into another organisation: go there, keeping the tab and the rest of the query.
          navigate(`/relaties/organisaties/${found.moved_to}${location.search}`, { replace: true })
          return
        }
        const answer = await membersAnswer
        if (cancelled) return
        if ('error' in answer) throw answer.error
        loaded.current = true
        setOrganisation(found)
        setMembers(answer.list)
        setLoadError(null)
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        if (caught instanceof CrmApiError && caught.status === 404) {
          setLoadError('notfound')
        } else if (loaded.current) {
          // A refresh that fails leaves the page as it was: it is not a reason to lose it.
          toast.error(caught instanceof Error ? caught.message : 'Verversen mislukt.')
        } else {
          setLoadError('failed')
        }
      })
    return () => {
      cancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `location.search` is only read at the moment of a redirect
  }, [organisationId, reloadKey])

  useEffect(() => {
    // The delete dialog was opened from the menu, and the menu item that had the focus is
    // gone by then: when the dialog closes the focus goes back to the menu button.
    if (confirmDelete) {
      askedDelete.current = true
    } else if (askedDelete.current) {
      askedDelete.current = false
      menuButton.current?.focus()
    }
  }, [confirmDelete])

  const reload = () => setReloadKey((key) => key + 1)
  // Something happened that the timeline, Gepland and the recent activity show too (an activity,
  // a task, a contact person): they load again next to the organisation itself.
  const refreshAll = () => {
    setVersion((value) => value + 1)
    reload()
  }
  const { data: agreements, failed: agreementsFailed } = useRelationAgreements(relation, version)

  /**
   * The answer to a change of the fields. The labels have their own path
   * (`changeLabels`); and an answer that is older than what is on the screen is
   * dropped: requests can finish out of order.
   */
  function showFields(updated: CrmOrganisation) {
    setOrganisation((current) => {
      if (!current) return updated
      if (Date.parse(updated.updated_at) < Date.parse(current.updated_at)) return current
      return { ...updated, labels: current.labels }
    })
  }

  async function patch(fields: CrmOrganisationInput) {
    // Errors propagate: InlineText shows "Niet opgeslagen – opnieuw proberen".
    showFields(await updateOrganisation(organisationId, fields))
  }

  async function patchWithToast(fields: CrmOrganisationInput, success?: string) {
    try {
      await patch(fields)
      if (success) toast.success(success)
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Opslaan mislukt.')
    }
  }

  const { changeOwner, dialog: ownerDialog } = useOwnerChange(relation, Boolean(organisation?.owner), (fields) =>
    patchWithToast(fields),
  )

  /**
   * Label changes are shown at once and sent one after the other, each with the whole
   * set as it stood after that click. Built on the last answer instead, a quick second
   * click would send a set without the first one and take it away again.
   */
  function changeLabels(next: LabelLike[]) {
    setChosenLabels(next)
    labelSaves.current += 1
    labelQueue.current = labelQueue.current.then(async () => {
      try {
        const saved = await setOrganisationLabels(
          organisationId,
          next.map((label) => label.id),
        )
        setOrganisation((current) => current && { ...current, labels: saved.labels })
      } catch (caught) {
        toast.error(caught instanceof Error ? caught.message : 'Labels opslaan mislukt.')
      } finally {
        labelSaves.current -= 1
        if (labelSaves.current === 0) setChosenLabels(null)
      }
    })
  }

  function selectTab(next: TabKey) {
    setParams(pageSearch(next, null, includeMembers), { state: location.state })
  }

  function selectChip(key: ChipKey | null) {
    setParams(pageSearch('activiteit', key, includeMembers), { replace: true, state: location.state })
  }

  function changeIncludeMembers(include: boolean) {
    setParams(pageSearch(tab, typeKey, include), { replace: true, state: location.state })
  }

  /** From a link inside the page: the tab opens, and the focus goes to it like it does with the arrow keys. */
  function showPersons() {
    selectTab('contactpersonen')
    focusTab(id, 'contactpersonen')
  }

  /** "Alles →" of Recente activiteit: the button is gone once the tab has changed, so the focus goes to the tab. */
  function showActivity() {
    selectTab('activiteit')
    focusTab(id, 'activiteit')
  }

  /**
   * A quick action: the composer opens on Overzicht (or stays on Activiteit) with the focus in
   * it. Every press is a request of its own, also while the composer is open already.
   */
  function openComposer(type: ComposerType) {
    if (tab !== 'overzicht' && tab !== 'activiteit') selectTab('overzicht')
    setComposer({ open: true, type, focus: true })
  }

  function changeAddOpen(next: boolean) {
    setAddOpen(next)
    if (!next && params.get('toevoegen')) {
      const copy = new URLSearchParams(params)
      copy.delete('toevoegen')
      setParams(copy, { replace: true, state: location.state })
    }
  }

  function chooseFromMenu(action: () => void) {
    setMenuOpen(false)
    action()
  }

  async function remove(current: CrmOrganisation) {
    setDeleting(true)
    try {
      await deleteOrganisation(current.id)
      setConfirmDelete(false)
      navigate(listPath)
      toastUndo(`Organisatie '${current.name}' verwijderd.`, async () => {
        await restoreOrganisation(current.id)
        navigate(`/relaties/organisaties/${current.id}`)
      })
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Verwijderen mislukt.')
    } finally {
      setDeleting(false)
    }
  }

  if (loadError) {
    return loadError === 'notfound' ? (
      <ErrorCard
        title="Organisatie niet gevonden"
        description="Deze organisatie bestaat niet (meer). Misschien is ze verwijderd."
        onRetry={reload}
      />
    ) : (
      <ErrorCard title="Organisatie laden mislukt" onRetry={reload} />
    )
  }
  if (!organisation || !members) {
    return (
      <div className="flex flex-col gap-4" aria-hidden>
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  const editAgreement = (agreement: Agreement) => setAgreementDialog({ open: true, agreement })
  const runningAgreements = agreements
    ? [...agreements.own, ...agreements.via].filter((agreement) => agreement.status === 'actief').length
    : null
  const current = members.items.filter((member) => !member.ended_on)
  const archived = organisation.status === 'inactief'
  const labels = chosenLabels ?? organisation.labels
  const href = webHref(organisation.website)
  const host = organisation.domains[0] ?? organisation.website

  const actions: QuickAction[] = [
    { key: 'note', label: 'Notitie', icon: ACTIVITY_ICONS.note, onClick: () => openComposer('note') },
    { key: 'call', label: 'Gesprek', icon: ACTIVITY_ICONS.call, onClick: () => openComposer('call') },
    ...inspreken.quick,
    { key: 'meeting', label: 'Meeting', icon: ACTIVITY_ICONS.meeting, onClick: () => openComposer('meeting') },
    { key: 'task', label: 'Taak', icon: ListChecks, onClick: () => setTaskOpen(true) },
    { key: 'agreement', label: 'Afspraak', icon: Handshake, onClick: () => setAgreementDialog({ open: true }) },
    { key: 'member', label: 'Contactpersoon', icon: UserPlus, onClick: () => changeAddOpen(true) },
  ]

  const header = (
    <>
      <div className="flex items-start gap-3">
        <EntityAvatar kind="company" name={organisation.name} size="xl" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="font-heading text-xl font-semibold break-words">{organisation.name}</h1>
          {organisation.description ? (
            <p className="text-sm text-muted-foreground">{organisation.description}</p>
          ) : null}
          {archived ? <p className="text-xs font-medium text-muted-foreground">Gearchiveerd</p> : null}
        </div>
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <Button ref={menuButton} type="button" size="sm" variant="ghost">
              Acties
              <MoreHorizontal />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="flex w-48 flex-col p-1">
            <button
              type="button"
              className={menuItemClass}
              onClick={() =>
                chooseFromMenu(() =>
                  archived
                    ? void patchWithToast({ status: 'actief' }, 'Organisatie weer actief.')
                    : setArchiveOpen(true),
                )
              }
            >
              {archived ? 'Heractiveren' : 'Archiveren'}
            </button>
            <button
              type="button"
              className={menuItemClass}
              onClick={() => chooseFromMenu(() => setMergePicking(true))}
            >
              Samenvoegen met…
            </button>
            <button
              type="button"
              className={cn(menuItemClass, 'text-destructive')}
              onClick={() => chooseFromMenu(() => setConfirmDelete(true))}
            >
              Verwijderen
            </button>
          </PopoverContent>
        </Popover>
      </div>
      {organisation.category ? <CategoryChip category={organisation.category} size="md" /> : null}
      <div role="group" aria-label="Labels" className="flex flex-wrap items-center gap-1">
        {labels.map((label) => (
          <LabelChip
            key={label.id}
            label={label}
            onRemove={() => changeLabels(labels.filter((item) => item.id !== label.id))}
          />
        ))}
        <LabelPicker selected={labels} onChange={changeLabels} />
      </div>
      {organisation.phone ? (
        <p className="text-sm">
          <PhoneLink
            phone={organisation.phone}
            relation={{ type: 'organisatie', id: organisationId, name: organisation.name }}
          />
        </p>
      ) : null}
      {organisation.website || organisation.city ? (
        <p className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
          {organisation.website ? (
            href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline"
              >
                {host}
                <ExternalLink className="size-3.5" aria-hidden />
              </a>
            ) : (
              <span className="text-foreground">{host}</span>
            )
          ) : null}
          {organisation.website && organisation.city ? <span>·</span> : null}
          {organisation.city ? <span>{organisation.city}</span> : null}
        </p>
      ) : null}
      <QuickActions actions={actions} />
    </>
  )

  const properties = (
    <>
      <PropertySection title="Over deze organisatie" storageKey="organisatie-over">
        <InlineProperty
          label="Naam"
          value={organisation.name}
          required
          maxLength={200}
          onSave={(value) => patch({ name: value ?? organisation.name })}
        />
        <InlineProperty
          label="Omschrijving"
          value={organisation.description}
          maxLength={300}
          onSave={(value) => patch({ description: value })}
        />
        <InlineProperty
          label="Website"
          type="url"
          value={organisation.website}
          onSave={(value) => patch({ website: value })}
        />
        <InlineProperty
          label="Domeinen"
          value={organisation.domains.length > 0 ? organisation.domains.join(', ') : null}
          onSave={(value) => patch({ domains: value ? value.split(/[\s,]+/).filter(Boolean) : [] })}
        />
        <InlineProperty
          label="KvK"
          value={organisation.kvk_number}
          onSave={(value) => patch({ kvk_number: value })}
        />
        <InlineProperty
          label="Straat"
          value={organisation.street}
          onSave={(value) => patch({ street: value })}
        />
        <InlineProperty
          label="Huisnummer"
          value={organisation.house_number}
          onSave={(value) => patch({ house_number: value })}
        />
        <InlineProperty
          label="Postcode"
          value={organisation.postcode}
          onSave={(value) => patch({ postcode: value })}
        />
        <InlineProperty
          label="Plaats"
          value={organisation.city}
          onSave={(value) => patch({ city: value })}
        />
        <InlineProperty
          label="Land"
          value={organisation.country}
          onSave={(value) => patch({ country: value })}
        />
        <InlineProperty
          label="Telefoon"
          type="tel"
          value={organisation.phone}
          onSave={(value) => patch({ phone: value })}
        />
        <InlineProperty
          label="Algemeen e-mail"
          type="email"
          value={organisation.email}
          onSave={(value) => patch({ email: value })}
        />
        <InlineProperty
          label="LinkedIn"
          type="url"
          value={organisation.linkedin_url}
          onSave={(value) => patch({ linkedin_url: value })}
        />
      </PropertySection>
      <PropertySection title="Relatie" storageKey="organisatie-relatie">
        <StaticProperty label="Categorie">
          <CategorySelect
            value={organisation.category}
            onChange={(category) => void patchWithToast({ category_id: category?.id ?? null })}
          />
        </StaticProperty>
        <StaticProperty label="Eigenaar">
          <select
            aria-label="Eigenaar"
            value={organisation.owner?.id ?? ''}
            onChange={(event) => changeOwner(event.target.value || null)}
            className={selectClass}
          >
            <option value="">Geen eigenaar</option>
            {organisation.owner && !membersById?.has(organisation.owner.id) ? (
              <option value={organisation.owner.id}>{organisation.owner.full_name}</option>
            ) : null}
            {team.map((member) => (
              <option key={member.id} value={member.id}>
                {member.display_name}
              </option>
            ))}
          </select>
        </StaticProperty>
        <StaticProperty label="Status">
          <select
            aria-label="Status"
            value={organisation.status}
            onChange={(event) =>
              void patchWithToast({ status: event.target.value === 'inactief' ? 'inactief' : 'actief' })
            }
            className={selectClass}
          >
            <option value="actief">Actief</option>
            <option value="inactief">Inactief</option>
          </select>
        </StaticProperty>
        <InlineProperty
          label="Relatie sinds"
          type="date"
          value={organisation.relation_since}
          onSave={(value) => patch({ relation_since: value })}
        />
        <StaticProperty label="Bron">
          {SOURCE_LABELS[organisation.source] ?? organisation.source}
        </StaticProperty>
      </PropertySection>
      <PropertySection title="Systeem" storageKey="organisatie-systeem" defaultOpen={false}>
        <StaticProperty label="Aangemaakt">
          {formatDate(organisation.created_at)}
          {organisation.created_by ? ` · ${organisation.created_by.full_name}` : ''}
        </StaticProperty>
        <StaticProperty label="Gewijzigd">{formatDate(organisation.updated_at)}</StaticProperty>
        {organisation.last_activity ? (
          <StaticProperty label="Laatste activiteit">{lastActivityText(organisation.last_activity)}</StaticProperty>
        ) : null}
      </PropertySection>
    </>
  )

  // One composer for both panels (only one of them renders): its draft lives in sessionStorage.
  const composerRelation: ComposerRelation = {
    ...relation,
    name: organisation.name,
    members: current.map((member) => ({ person_id: member.person_id, full_name: member.full_name })),
  }
  const composerCard = (
    <ActivityComposer
      relation={composerRelation}
      open={composer.open}
      type={composer.type}
      autoFocus={composer.focus}
      onAutoFocused={lowerFocusRequest}
      onOpenChange={(open) => setComposer((state) => ({ ...state, open, focus: false }))}
      onTypeChange={(type) => setComposer((state) => ({ ...state, type }))}
      onSaved={(item) => {
        if (item.type === 'call' || item.type === 'meeting') {
          clearCall()
          setCallEpoch((n) => n + 1)
        }
        refreshAll()
      }}
      onTaskCreated={refreshAll}
    />
  )
  const planned = <PlannedSection relation={relation} version={version} onChanged={refreshAll} showPerson />

  const overview = (
    <>
      <CallPromptBanner
        key={callEpoch}
        relation={{ type: 'organisatie', id: organisationId, name: organisation.name }}
        onLogCall={() => openComposer('call')}
      />
      <MergeNotice kind="organisatie" relationId={organisationId} survivorName={organisation.name} onUndone={refreshAll} />
      <RelationKeyFacts
        relationSince={organisation.relation_since}
        lastContact={organisation.last_contact}
        nextAction={organisation.next_action}
        rhythm={agreements ? rhythmState(agreements.own) : null}
      />
      {composerCard}
      <SummaryEditor
        title={`Over ${organisation.name}`}
        html={organisation.summary_html}
        placeholder={`Leg vast wat het team over ${organisation.name} moet weten…`}
        draftKey={`crm:over-organisatie:${organisation.id}`}
        edited={
          organisation.summary_updated_at
            ? { at: organisation.summary_updated_at, by: organisation.summary_updated_by?.full_name ?? null }
            : null
        }
        onSave={(html) => patch({ summary_html: html })}
      />
      <section aria-label="Contactpersonen in het kort" className="vonk-card flex flex-col gap-2 p-5">
        <h2 className="font-heading text-base font-semibold">Contactpersonen</h2>
        {current.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nog geen contactpersonen.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {current.slice(0, 5).map((member) => (
              <li key={member.id} className="flex items-center gap-2">
                <EntityAvatar kind="person" name={member.full_name} size="xs" />
                <Link
                  to={`/relaties/contacten/${member.person_id}`}
                  className="font-medium underline-offset-2 hover:underline"
                >
                  {member.full_name}
                </Link>
                {member.role ? <span className="text-muted-foreground">· {member.role}</span> : null}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={showPersons}
          className="self-start text-xs font-medium text-primary hover:underline max-md:min-h-11"
        >
          Alle contactpersonen →
        </button>
      </section>
      <AgreementsSection
        relation={relation}
        data={agreements}
        failed={agreementsFailed}
        onNew={() => setAgreementDialog({ open: true })}
        onEdit={editAgreement}
        onContact={() => openComposer('call')}
        onChanged={refreshAll}
      />
      {planned}
      <RecentActivity
        relation={relation}
        includeMembers={includeMembers}
        version={version}
        onShowAll={showActivity}
      />
    </>
  )

  // An activity edited, deleted or restored in the timeline may change "Laatste contact" and
  // "Volgende actie": the organisation loads again; the timeline updated itself.
  const activity = (
    <>
      {composerCard}
      <Timeline
        relation={relation}
        typeKey={typeKey}
        onTypeKeyChange={selectChip}
        includeMembers={includeMembers}
        onIncludeMembersChange={changeIncludeMembers}
        version={version}
        planned={planned}
        onChanged={reload}
      />
    </>
  )

  const personsCard = (
    <AssociationCard
      title="Contactpersonen"
      items={current}
      itemKey={(member) => member.id}
      onAdd={() => changeAddOpen(true)}
      addLabel="Contactpersoon toevoegen"
      emptyText="Nog geen contactpersonen."
      renderItem={(member) => (
        <div className="flex flex-col text-sm">
          <Link
            to={`/relaties/contacten/${member.person_id}`}
            className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
          >
            {member.full_name}
            {member.is_primary ? (
              <Star
                role="img"
                aria-label="primair"
                className="size-3.5 fill-amber-400 text-amber-500"
              />
            ) : null}
          </Link>
          {member.role ? <span className="text-muted-foreground">{member.role}</span> : null}
        </div>
      )}
      footer={
        members.former_count > 0 ? (
          <button
            type="button"
            onClick={showPersons}
            className="self-start text-xs text-muted-foreground hover:underline"
          >
            {members.former_count} voormalig
          </button>
        ) : null
      }
    />
  )

  const right = (
    <>
      {personsCard}
      <ListsCard lists={organisation.lists} />
    </>
  )

  return (
    <>
      <RecordLayout
        back={<RecordBackLink to={listPath}>Organisaties</RecordBackLink>}
        label="Organisatie"
        header={header}
        properties={properties}
        tabs={[
          { key: 'overzicht', label: 'Overzicht' },
          { key: 'contactpersonen', label: 'Contactpersonen', count: members.current_count },
          { key: 'activiteit', label: 'Activiteit' },
          { key: 'afspraken', label: 'Afspraken', ...(runningAgreements !== null ? { count: runningAgreements } : {}) },
        ]}
        activeTab={tab}
        onTabChange={(key) => selectTab(parseTab(key))}
        panels={{
          overzicht: overview,
          contactpersonen: (
            <MembersTab
              organisation={organisation}
              members={members}
              onChanged={reload}
              onAdd={() => changeAddOpen(true)}
            />
          ),
          activiteit: activity,
          afspraken: (
            <AgreementsTab
              relation={relation}
              name={organisation.name}
              data={agreements}
              failed={agreementsFailed}
              onNew={() => setAgreementDialog({ open: true })}
              onEdit={editAgreement}
              onContact={() => openComposer('call')}
              onChanged={refreshAll}
            />
          ),
        }}
        right={right}
        idPrefix={id}
      />
      <TaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        relation={{ ...relation, name: organisation.name }}
        onCreated={refreshAll}
        onMakeAgreement={() => setAgreementDialog({ open: true })}
      />
      {agreementDialog.open ? (
        <AgreementDialog
          open
          onOpenChange={(open) => {
            if (!open) setAgreementDialog({ open: false })
          }}
          party={{ kind: 'organisation', id: organisationId, name: organisation.name }}
          agreement={agreementDialog.agreement}
          defaultOwnerId={organisation.owner?.id}
          onSaved={refreshAll}
        />
      ) : null}
      {ownerDialog}
      <ArchiveRelationDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        relation={relation}
        name={organisation.name}
        onConfirm={(endAgreements) => {
          setArchiveOpen(false)
          void patchWithToast({ status: 'inactief', end_agreements: endAgreements }, 'Organisatie gearchiveerd.').then(refreshAll)
        }}
      />
      <AddMemberDialog
        organisation={organisation}
        open={addOpen}
        onOpenChange={changeAddOpen}
        onAdded={refreshAll}
      />
      {mergePicking ? (
        <PickOrganisationDialog
          excludeId={organisationId}
          onClose={() => setMergePicking(false)}
          onPicked={(pickedId) => {
            setMergePicking(false)
            setMergeWith(pickedId)
          }}
        />
      ) : null}
      {mergeWith ? (
        <MergeOrganisationsDialog
          firstId={organisationId}
          secondId={mergeWith}
          survivorId={organisationId}
          onClose={() => setMergeWith(null)}
          onUndone={refreshAll}
          onMerged={(survivor) => {
            setMergeWith(null)
            if (survivor.id === organisationId) refreshAll()
            else navigate(`/relaties/organisaties/${survivor.id}`)
          }}
        />
      ) : null}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Organisatie '${organisation.name}' verwijderen?`}
        description={deleteDescription(members.current_count + members.former_count)}
        busy={deleting}
        onConfirm={() => void remove(organisation)}
      />
    </>
  )
}
